import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider
} from '@heroui/react'
import { ShieldX, Mail, Globe } from 'lucide-react'
import { useTypedTranslation } from '@/hooks/useTypedTranslation'
import { useCountryLanguage } from '@/hooks/useCountryLanguage'
import { CountryBlockingState } from '@/types'
import { SOCIAL_LINKS } from '@/data/social'

interface CountryBlockedModalProps {
  isOpen: boolean
  countryBlocking: CountryBlockingState
}

export function CountryBlockedModal({ isOpen, countryBlocking }: CountryBlockedModalProps) {
  const { t } = useTypedTranslation(['errors'])

  // Switch to the blocked country's language if supported
  const {
    isLanguageSwitched,
    countryLanguage
  } = useCountryLanguage({
    countryCode: countryBlocking.userCountry,
    enabled: isOpen && !!countryBlocking.userCountry
  })

  const handleContactSupport = () => {
    // Open mail client with support email
    window.open(SOCIAL_LINKS.telegram, '_blank')
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { }} // Prevent closing - this is a blocking modal
      hideCloseButton
      backdrop="blur"
      classNames={{
        backdrop: "bg-black/80",
        base: "border border-divider",
        body: "py-6",
        header: "border-b border-divider",
        footer: "border-t border-divider"
      }}
      size="lg"
      placement="center"
      isDismissable={false}
      isKeyboardDismissDisabled
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center gap-3 rtl:gap-x-reverse">
            <div className="p-2 bg-danger/10 rounded-lg">
              <ShieldX className="w-8 h-8 text-danger" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-danger">
                {t('errors:countryBlocking.title')}
              </h2>
              {countryBlocking.userCountry && (
                <div className="flex flex-col gap-1 mt-1">
                  <div className="flex items-center gap-2 rtl:gap-x-reverse">
                    <Globe className="w-4 h-4 text-default-400" />
                    <span className="text-sm text-default-400">
                      {countryBlocking.userCountry}
                    </span>
                  </div>
                  {isLanguageSwitched && countryLanguage && (
                    <div className="text-xs text-success">
                      {t('errors:countryBlocking.languageIndicator', {
                        language: countryLanguage.toUpperCase()
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </ModalHeader>

        <ModalBody>
          <div className="space-y-4">
            {/* Main Message */}
            <Card className="bg-danger/5 border border-danger/20">
              <CardBody>
                <p className="text-center font-medium text-foreground">
                  {t('errors:countryBlocking.message')}
                </p>
              </CardBody>
            </Card>

            {/* Explanation */}
            <div>
              <p className="text-default-700 leading-relaxed text-center">
                {t('errors:countryBlocking.description')}
              </p>
            </div>

            <Divider />

            {/* Support Section */}
            <Card className="bg-default-50">
              <CardHeader>
                <h3 className="text-lg font-semibold flex items-center gap-2 rtl:gap-x-reverse">
                  <Mail className="w-5 h-5" />
                  {t('errors:countryBlocking.supportTitle')}
                </h3>
              </CardHeader>
              <CardBody>
                <p className="text-default-600 mb-4">
                  {t('errors:countryBlocking.supportMessage')}
                </p>
                <Button
                  color="primary"
                  variant="flat"
                  startContent={<Mail className="w-4 h-4" />}
                  onPress={handleContactSupport}
                  className="w-full"
                >
                  {t('errors:countryBlocking.contactSupport')}
                </Button>
              </CardBody>
            </Card>

            {/* Timestamp */}
            {countryBlocking.blockedAt && (
              <div className="text-center">
                <p className="text-xs text-default-400">
                  {t('errors:countryBlocking.blockedAt', {
                    timestamp: countryBlocking.blockedAt.toLocaleString()
                  })}
                </p>
              </div>
            )}
          </div>
        </ModalBody>

        <ModalFooter>
          <div className="w-full text-center">
            <p className="text-sm text-default-400">
              {t('errors:countryBlocking.automaticRestriction')}
            </p>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}